import {
  ConfirmationAction,
  ConfirmationBody,
  ConfirmationModalConfig,
} from '../../../presentation/components/confirmation-modal';

/** Standard cancel button shared by every preset. */
const cancelAction = (label = 'Cancel'): ConfirmationAction => ({
  label,
  value: 'cancel',
  autIdSuffix: 'cancel-btn',
  variant: 'basic',
  color: 'primary',
});

/**
 * Body overrides accepted by every preset. A component or a template wins over the preset's
 * default text; `message`/`items` refine it.
 */
type PresetBodyOverride = Partial<
  Pick<
    ConfirmationModalConfig,
    'message' | 'items' | 'bodyComponent' | 'bodyInputs' | 'bodyTemplate'
  >
>;

/** Dialog size/width pass-through, for the call sites that need a narrower dialog. */
type PresetLayoutOverride = Partial<Pick<ConfirmationModalConfig, 'size' | 'width'>>;

/**
 * Picks exactly one body shape so the rendered dialog never mixes text with custom content.
 */
const resolveBody = (defaultMessage: string, options: PresetBodyOverride): ConfirmationBody => {
  if (options.bodyComponent) {
    return { bodyComponent: options.bodyComponent, bodyInputs: options.bodyInputs };
  }

  if (options.bodyTemplate) {
    return { bodyTemplate: options.bodyTemplate };
  }

  return { message: options.message ?? defaultMessage, items: options.items };
};

export interface DeleteConfirmationOptions extends PresetBodyOverride, PresetLayoutOverride {
  /** `aut-id` prefix. @example 'delete-department' */
  autId: string;
  /** What is being deleted, lowercase. @example 'department', 'case type' */
  entity: string;
  /** Name of the specific record, when there is one. */
  name?: string;
  /** Overrides the generated title. */
  title?: string;
  /** @default 'Delete' */
  confirmLabel?: string;
  disclaimer?: string;
}

/**
 * Destructive delete: warning badge, red confirm button, "cannot be undone" copy.
 */
export const deleteConfirmation = (options: DeleteConfirmationOptions): ConfirmationModalConfig => {
  const target = options.name ? `"${options.name}"` : `this ${options.entity}`;

  return {
    autId: options.autId,
    title: options.title ?? `Delete ${options.entity}?`,
    titleIcon: { name: 'warning', tone: 'danger' },
    disclaimer: options.disclaimer,
    size: options.size,
    width: options.width,
    confirm: {
      label: options.confirmLabel ?? 'Delete',
      value: 'confirm',
      autIdSuffix: 'confirm-btn',
      variant: 'flat',
      color: 'danger',
    },
    cancel: cancelAction(),
    ...resolveBody(
      `Are you sure you want to delete ${target}? This action cannot be undone.`,
      options,
    ),
  };
};

export interface ToggleActiveConfirmationOptions
  extends PresetBodyOverride,
    PresetLayoutOverride {
  autId: string;
  /** What is being toggled, lowercase. @example 'routing rule' */
  entity: string;
  name?: string;
  /** `true` activates, `false` deactivates. Drives the copy and the button label. */
  activate: boolean;
  /** Verb pair used in the copy. @default ['Activate', 'Deactivate'] */
  labels?: [activate: string, deactivate: string];
  title?: string;
}

/**
 * Activate/deactivate (or enable/disable) confirmation: informational badge, primary confirm.
 */
export const toggleActiveConfirmation = (
  options: ToggleActiveConfirmationOptions,
): ConfirmationModalConfig => {
  const [activateLabel, deactivateLabel] = options.labels ?? ['Activate', 'Deactivate'];
  const label = options.activate ? activateLabel : deactivateLabel;
  const target = options.name ? `"${options.name}"` : `this ${options.entity}`;

  return {
    autId: options.autId,
    title: options.title ?? `${label} ${options.entity}?`,
    titleIcon: { name: 'help', tone: 'info' },
    size: options.size,
    width: options.width,
    confirm: {
      label,
      value: 'confirm',
      autIdSuffix: 'confirm-btn',
      variant: 'flat',
      color: 'primary',
    },
    cancel: cancelAction(),
    ...resolveBody(`Are you sure you want to ${label.toLowerCase()} ${target}?`, options),
  };
};

export interface DiscardConfirmationOptions extends PresetBodyOverride, PresetLayoutOverride {
  /** @default 'discard-changes' */
  autId?: string;
  /** @default 'Discard changes?' */
  title?: string;
  /** @default 'Discard' */
  confirmLabel?: string;
  /** @default 'Keep editing' */
  cancelLabel?: string;
}

/**
 * Unsaved-changes confirmation.
 */
export const discardConfirmation = (
  options: DiscardConfirmationOptions = {},
): ConfirmationModalConfig => ({
  autId: options.autId ?? 'discard-changes',
  title: options.title ?? 'Discard changes?',
  titleIcon: { name: 'warning', tone: 'warning' },
  size: options.size,
  width: options.width,
  confirm: {
    label: options.confirmLabel ?? 'Discard',
    value: 'confirm',
    autIdSuffix: 'confirm-btn',
    variant: 'flat',
    color: 'danger',
  },
  cancel: cancelAction(options.cancelLabel ?? 'Keep editing'),
  ...resolveBody('You have unsaved changes. If you leave, your changes will be lost.', options),
});

export interface BlockedConfirmationOptions extends PresetBodyOverride, PresetLayoutOverride {
  autId: string;
  title: string;
  /** @default 'Close' */
  confirmLabel?: string;
}

/**
 * Informational/blocking dialog: a single action and no cancel — the user cannot proceed with
 * the original intent (e.g. "Can't delete: the status is still in use").
 */
export const blockedConfirmation = (
  options: BlockedConfirmationOptions,
): ConfirmationModalConfig => ({
  autId: options.autId,
  title: options.title,
  titleIcon: { name: 'error', tone: 'danger' },
  size: options.size,
  width: options.width,
  confirm: {
    label: options.confirmLabel ?? 'Close',
    value: 'confirm',
    autIdSuffix: 'confirm-btn',
    variant: 'flat',
    color: 'primary',
  },
  cancel: false,
  ...resolveBody('', options),
});
